<script setup lang="ts">
  import { ref } from "vue";
  import { Plus, Type, Upload } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { Spinner } from "@/components/ui/spinner";
  import type { SurveyContextSource } from "@/types";
  import { formatFileSize } from "@utils/media";
  import ContextSourceItem from "./ContextSourceItem.vue";
  import { ACCEPT_ATTRIBUTE, ACCEPTED_FAMILIES, MAX_FILE_SIZE, MAX_SOURCES } from "./contextSources";

  defineProps<{ sources: SurveyContextSource[]; surveyToken: string; uploading: boolean }>();
  const emit = defineEmits<{
    addFiles: [files: File[]];
    pasteText: [];
    edit: [token: string];
    remove: [token: string];
  }>();

  const fileInput = ref<HTMLInputElement | null>(null);
  const isDragging = ref(false);

  const browse = () => fileInput.value?.click();

  const onPick = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length) emit("addFiles", files);
    input.value = "";
  };

  const onDrop = (event: DragEvent) => {
    isDragging.value = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length) emit("addFiles", files);
  };
</script>

<template>
  <section
    aria-label="Sources"
    class="flex min-h-0 flex-col gap-3 overflow-hidden rounded-lg border p-4 transition-colors"
    :class="{ 'border-primary bg-muted': isDragging && sources.length }"
    @dragover.prevent="isDragging = true"
    @dragleave.self="isDragging = false"
    @drop.prevent="onDrop">
    <h2 class="heading-title shrink-0">
      Sources
      <span class="ml-1 font-normal text-muted-foreground">{{ sources.length }}</span>
    </h2>

    <div
      v-if="sources.length"
      class="-mr-2 flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-2">
      <ContextSourceItem
        v-for="source in sources"
        :key="source.token"
        :source="source"
        :survey-token="surveyToken"
        @edit="emit('edit', $event)"
        @remove="emit('remove', $event)" />
    </div>

    <div
      v-else
      data-cy="context-dropzone"
      class="flex flex-col items-center gap-1 rounded-lg border-2 border-dashed bg-muted px-3 py-6 text-center transition-colors"
      :class="isDragging ? 'border-primary' : 'border-border-strong'">
      <Upload
        class="size-4.5 text-muted-foreground"
        :stroke-width="1.8" />
      <div class="text-13 text-foreground-secondary">
        Drop files,
        <button
          type="button"
          class="font-medium text-primary underline-offset-4 hover:underline"
          @click="browse">
          browse
        </button>
        or
        <button
          type="button"
          class="font-medium text-primary underline-offset-4 hover:underline"
          @click="emit('pasteText')">
          paste text
        </button>
      </div>
      <div class="text-2xs text-muted-foreground">
        {{ ACCEPTED_FAMILIES }} · {{ formatFileSize(MAX_FILE_SIZE) }} per file
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="ACCEPT_ATTRIBUTE"
      multiple
      @change="onPick" />

    <div class="mt-auto shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            variant="outline"
            class="w-full"
            :disabled="uploading || sources.length >= MAX_SOURCES">
            <Spinner v-if="uploading" />
            <Plus v-else />
            Add source
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem @select="browse">
            <Upload />
            Upload files
          </DropdownMenuItem>
          <DropdownMenuItem @select="emit('pasteText')">
            <Type />
            Paste text
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </section>
</template>
